import { max } from 'd3';

import Axis from '../Base/axis';
import Data from '../Base/data';
import Dims from '../Base/dims';
import Margin from '../Base/margin';
import Svg from '../Base/svg';
import Transforms from '../Base/transforms';
import ToolTip from '../ToolTip';
import { BubbleCtrParams } from '../types';
import Utils from '../Utils';

/**
 * Bubble Plot Class
 */
export class Bubble {
  private elementId: string;
  private element: Svg;

  private dims: Dims;
  private margin: Margin;

  private toolTip: ToolTip;
  private useToolTip = false;
  private animators: any;

  public data: Data;
  private _xField = 'x';
  private _yField = 'y';
  private _zField = 'z';

  public xAxis: Axis;
  public yAxis: Axis;
  public zAxis: Axis;

  public transforms: Transforms;

  /**
   * Constructs a new Bubble Plot instance
   * @param {BubbleCtrParams} params
   * @returns {Bubble}
   */
  constructor(params: BubbleCtrParams = {}) {
    this.elementId = params.elementId;

    this.dims = new Dims(params.height, params.width);
    this.margin = new Margin(params.margin);

    this.data = new Data(params.data);

    this._xField = params.xField ?? 'x';
    this._yField = params.yField ?? 'y';
    this._zField = params.zField ?? 'z';

    const xTransform = params.xTransform ?? 'linear';
    const yTransform = params.yTransform ?? 'linear';
    const zTransform = params.zTransform ?? 'linear';

    this.transforms = new Transforms({ [this._xField]: xTransform, [this._yField]: yTransform, [this._zField]: zTransform });

    this.yAxis = new Axis({ loc: 'left' });
    this.xAxis = new Axis({ loc: 'bottom' });
    this.zAxis = new Axis({ loc: 'none' });
    this._useAutoAxis();
  }

  /**
   * Handles rendering the actual plot on the dom
   */
  public render() {
    const { elementId, margin } = this;
    const { height, width } = this.dims.both();
    this.element = new Svg({ elementId, height, width, margin });
    this.element.draw();

    this._drawXAxis();
    this._drawYAxis();
    this._drawPoints();
  }

  /**
   * Draws the x axis
   */
  private _drawXAxis() {
    const { d3Svg } = this.element;
    const { height: h } = this.dims.marDims(this.margin);

    const translate = `translate(0, ${h})`;
    Utils.drawAxis(d3Svg, this.xAxis, { translate });
  }

  /**
   * Draws the y axis
   */
  private _drawYAxis() {
    const { d3Svg } = this.element;
    Utils.drawAxis(d3Svg, this.yAxis);
  }

  /**
   * Draws the 'bubbles' or points with respect to the instance's data.
   * Uses the z axis to determine the bubble's sizing
   */
  private _drawPoints() {
    const { xAxis, yAxis, zAxis, _xField, _yField, _zField } = this;
    const data = this.data.consume();

    const attr = {
      cx: (d) => xAxis.scale(d[_xField]),
      cy: (d) => yAxis.scale(d[_yField]),
      r: (d) => zAxis.scale(d[_zField]),
      stroke: '#DEDFDF',
    };

    const styles = {
      fill: 'steelblue',
      opacity: 0.7,
    };

    Utils.drawCircles(this.element.d3Svg, data, { attr, styles });
  }

  /**
   * Hook to automatically configure the domain, ranges, and scales
   * of the instance's axis'
   */
  private _useAutoAxis() {
    const { height: h, width: w } = this.dims.marDims(this.margin);
    // TODO: use data to set domains
    const data = this.data.consume();

    const xDomain = [0, 10000];
    const xRange = [0, w];
    const xScaleFn = this.transforms.getScale(this._xField);
    const xScale = xScaleFn().domain(xDomain).range(xRange);
    this.xAxis.setDomain(xDomain);
    this.xAxis.setRange(xRange);
    this.xAxis.setScale(xScale);

    const yDomain = [35, 90];
    const yRange = [h, 0];
    const yScaleFn = this.transforms.getScale(this._yField);
    const yScale = yScaleFn().domain(yDomain).range(yRange);
    this.yAxis.setDomain(yDomain);
    this.yAxis.setRange(yRange);
    this.yAxis.setScale(yScale);

    const zDomain = [200000, 1310000000];
    const zRange = [1, 40];
    const zScaleFn = this.transforms.getScale(this._zField);
    const zScale = zScaleFn().domain(zDomain).range(zRange);
    this.zAxis.setDomain(zDomain);
    this.zAxis.setRange(zRange);
    this.zAxis.setScale(zScale);
  }
}
